import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { camelCase, clone, get, groupBy, isObject, map } from 'lodash';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import { Button } from '../../button/button';
import { InputComponent } from '..';
import { normalizeSize } from '../helpers';

export type InputMultiSelectOption =
  | {
      value: string | number;
      label: string;
      disabled?: boolean;
      group?: string;
    }
  | string
  | number;

export interface InputMultiSelectProps extends InputComponent {
  options?: InputMultiSelectOption[];
  sx?: TextFieldProps['sx'];
}

interface InternalOption {
  label: string;
  value: string | number;
  isActive?: boolean;
  disabled?: boolean;
  group?: string;
}

/**
 * Reducer function for managing multi-select filter state.
 * Handles adding/removing options, selecting all, clearing all, and selecting only one option.
 * @param allFilters - The current array of filter options
 * @param action - The action to perform on the filters
 * @returns The updated array of filter options
 */
function filterReducer(
  allFilters: InternalOption[],
  action:
    | { type: 'add_option' | 'remove_option'; idx: number }
    | { type: 'select_all' }
    | { type: 'clear_all' }
    | { type: 'only'; idx: number }
    | { type: 'set_selected'; selectedValues: (string | number)[] },
): InternalOption[] {
  // Clone to avoid mutations
  const newFilters = clone(allFilters);

  switch (action.type) {
    case 'add_option':
      newFilters[action.idx].isActive = true;
      break;
    case 'remove_option':
      newFilters[action.idx].isActive = false;
      break;
    case 'select_all':
      newFilters.forEach((filter) => {
        if (!filter.disabled) {
          // eslint-disable-next-line no-param-reassign
          filter.isActive = true;
        }
      });
      break;
    case 'clear_all':
      newFilters.forEach((filter) => {
        // eslint-disable-next-line no-param-reassign
        filter.isActive = false;
      });
      break;
    case 'only':
      newFilters.forEach((filter, idx) => {
        // eslint-disable-next-line no-param-reassign
        filter.isActive = idx === action.idx;
      });
      break;
    case 'set_selected':
      newFilters.forEach((filter) => {
        // eslint-disable-next-line no-param-reassign
        filter.isActive = action.selectedValues.includes(filter.value);
      });
      break;

    default:
      throw Error('Unknown action.');
  }

  return newFilters;
}

interface OptionMenuItemProps {
  option: InternalOption;
  idx: number;
  dispatchAndNotify: (action: Parameters<typeof filterReducer>[1]) => void;
  isGrouped?: boolean;
}

/**
 * Individual option menu item with checkbox and hover-only "Only" button
 * @param props - Component props
 * @param props.option - The option to render
 * @param props.idx - The index of the option in the allOptions array
 * @param props.dispatchAndNotify - Function to dispatch actions and notify Formik
 * @param props.isGrouped - Whether the option is part of a group (determines indentation)
 * @returns A MenuItem component with checkbox and "Only" button
 */
function OptionMenuItem({
  option,
  idx,
  dispatchAndNotify,
  isGrouped = false,
}: OptionMenuItemProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <MenuItem
      key={String(option.value)}
      value={option.value}
      disabled={option.disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px 8px',
        pl: isGrouped ? 3 : 1,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        onClick={(e) => {
          e.stopPropagation();
          if (!option.disabled) {
            dispatchAndNotify({
              type: option.isActive ? 'remove_option' : 'add_option',
              idx,
            });
          }
        }}
        sx={{
          flex: 1,
          cursor: option.disabled ? 'not-allowed' : 'pointer',
          padding: '4px 0',
        }}
      >
        <Checkbox
          checked={option.isActive}
          disabled={option.disabled}
          size="small"
          sx={{ padding: '4px' }}
        />
        <Typography variant="body2" sx={{ ml: 1 }}>
          {option.label}
        </Typography>
      </Box>
      {!option.disabled && (
        <Button
          content="Only"
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            dispatchAndNotify({ type: 'only', idx });
          }}
          sx={{
            minWidth: 'auto',
            padding: '2px 8px',
            fontSize: '0.75rem',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease',
          }}
        />
      )}
    </MenuItem>
  );
}

export function InputMultiSelect({
  label,
  options = [],
  disabled,
  hint,
  required,
  nameOverride,
  hidden,
  size: propSize,
  readOnly = false,
  sx = {},
}: InputMultiSelectProps) {
  const { values, setFieldValue, errors } =
    useFormikContext<Record<string, (string | number)[]>>();

  const validate = useCallback(
    (value: (string | number)[] | undefined) => {
      if (required && (!value || value.length === 0)) {
        return `${label} is required.`;
      }
      return undefined;
    },
    [label, required],
  );

  const name = useMemo(
    () => nameOverride ?? camelCase(label ?? ''),
    [label, nameOverride],
  );

  const error = useMemo(() => get(errors, name), [errors, name]);

  const size = normalizeSize(propSize);

  const [shown, setShown] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Convert options to internal format with isActive flag
  const normalizedOptions: InternalOption[] = useMemo(
    () =>
      options.map((option) => {
        if (isObject(option)) {
          return {
            value: option.value,
            label: option.label,
            disabled: option.disabled,
            group: option.group,
            isActive: false,
          };
        }
        return {
          value: typeof option === 'string' ? camelCase(option) : option,
          label: String(option),
          isActive: false,
        };
      }),
    [options],
  );

  const selectedValues = useMemo(
    () => (get(values, name) as (string | number)[]) ?? [],
    [name, values],
  );

  const [allOptions, dispatchOptionAction] = useReducer(
    filterReducer,
    normalizedOptions.map((option) => ({
      ...option,
      isActive: selectedValues.includes(option.value),
    })),
  );

  // Update internal state when selectedValues prop changes
  useEffect(() => {
    dispatchOptionAction({ type: 'set_selected', selectedValues });
  }, [selectedValues]);

  /**
   * Helper function to dispatch action and update Formik state.
   * Calculates the new selected values after the action and updates the form field.
   * @param action - The action to dispatch
   */
  const dispatchAndNotify = (action: Parameters<typeof filterReducer>[1]) => {
    dispatchOptionAction(action);

    // Calculate new values after the action
    const newOptions = clone(allOptions);
    switch (action.type) {
      case 'add_option':
        newOptions[action.idx].isActive = true;
        break;
      case 'remove_option':
        newOptions[action.idx].isActive = false;
        break;
      case 'select_all':
        newOptions.forEach((option) => {
          if (!option.disabled) {
            // eslint-disable-next-line no-param-reassign
            option.isActive = true;
          }
        });
        break;
      case 'clear_all':
        newOptions.forEach((option) => {
          // eslint-disable-next-line no-param-reassign
          option.isActive = false;
        });
        break;
      case 'only':
        newOptions.forEach((option, idx) => {
          // eslint-disable-next-line no-param-reassign
          option.isActive = idx === action.idx;
        });
        break;
      default:
        // No-op for 'set_selected' as it's handled by useEffect
        break;
    }

    const newValues = newOptions
      .filter((option) => option.isActive)
      .map((option) => option.value);

    setFieldValue(name, newValues);
  };

  /**
   * Formats the display text for the selected values.
   * Shows "None selected", "All selected", or a summary of selected options.
   * @returns The formatted display text
   */
  const getSelectedText = () => {
    const selectedOptions = allOptions.filter((option) => option.isActive);

    if (selectedOptions.length === 0) return 'None selected';
    if (selectedOptions.length === allOptions.length) return 'All selected';

    if (selectedOptions.length <= 2) {
      return selectedOptions.map((option) => option.label).join(' or ');
    }
    const first = selectedOptions[0].label;
    const second = selectedOptions[1].label;
    const remaining = selectedOptions.length - 2;
    return `${first}, ${second} + ${remaining}`;
  };

  return (
    <Field name={name} validate={validate}>
      {() => (
        <FormControl
          size={size as 'small' | 'medium'}
          fullWidth
          ref={anchorRef}
          disabled={disabled || readOnly}
          hidden={hidden}
          error={!!error}
          sx={sx}
        >
          <InputLabel required={required && !readOnly}>{label}</InputLabel>
          <Select
            multiple
            value={selectedValues}
            input={<OutlinedInput label={label} />}
            open={shown}
            onOpen={() => setShown(true)}
            onClose={() => setShown(false)}
            renderValue={() => getSelectedText()}
            MenuProps={{
              PaperProps: {
                sx: { minWidth: 300 },
              },
            }}
          >
            {/* Select All option with checkbox and Clear button */}
            <MenuItem
              sx={{
                padding: '4px 8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                top: 0,
                position: 'sticky',
                zIndex: 3,
                backgroundColor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  backgroundColor: 'background.paper',
                },
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                onClick={(e) => {
                  e.stopPropagation();
                  const allSelected = allOptions.every(
                    (option) => option.isActive || option.disabled,
                  );
                  dispatchAndNotify({
                    type: allSelected ? 'clear_all' : 'select_all',
                  });
                }}
                sx={{
                  flex: 1,
                  cursor: 'pointer',
                }}
              >
                <Checkbox
                  checked={allOptions.every(
                    (option) => option.isActive || option.disabled,
                  )}
                  indeterminate={
                    allOptions.some((option) => option.isActive) &&
                    !allOptions.every(
                      (option) => option.isActive || option.disabled,
                    )
                  }
                  size="small"
                  sx={{ padding: '4px' }}
                />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="primary"
                  sx={{ ml: 1 }}
                >
                  Select All
                </Typography>
              </Box>
              <Button
                content="Clear"
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatchAndNotify({ type: 'clear_all' });
                }}
                sx={{
                  minWidth: 'auto',
                  padding: '2px 8px',
                  fontSize: '0.75rem',
                }}
              />
            </MenuItem>

            {/* Individual options with checkboxes and hover-only Only button */}
            {(() => {
              // Check if any options have groups
              const hasGroups = allOptions.some((option) => option.group);

              if (!hasGroups) {
                // Render flat list if no groups
                return map(allOptions, (option, idx) => (
                  <OptionMenuItem
                    key={String(option.value)}
                    option={option}
                    idx={idx}
                    dispatchAndNotify={dispatchAndNotify}
                  />
                ));
              }

              // Render grouped options
              const grouped = groupBy(allOptions, (option) => option.group);
              const groupNames = Object.keys(grouped);

              return groupNames.map((groupName, groupIdx) => {
                const groupOptions = grouped[groupName];

                return (
                  <div key={groupName || `group-${groupIdx}`}>
                    {groupName && (
                      <ListSubheader
                        sx={{
                          backgroundColor: 'background.paper',
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          lineHeight: '32px',
                          color: 'text.primary',
                          position: 'sticky',
                          top: '38px', // Position below the Select All bar
                          zIndex: 2,
                        }}
                      >
                        {groupName}
                      </ListSubheader>
                    )}
                    {map(groupOptions, (option) => {
                      const idx = allOptions.indexOf(option);
                      return (
                        <OptionMenuItem
                          key={String(option.value)}
                          option={option}
                          idx={idx}
                          dispatchAndNotify={dispatchAndNotify}
                          isGrouped
                        />
                      );
                    })}
                  </div>
                );
              });
            })()}

            {/* Fixed Done button at the bottom */}
            <Divider />
            <MenuItem
              sx={{
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'background.paper',
                borderTop: '1px solid',
                borderColor: 'divider',
                padding: '8px',
                display: 'flex',
                justifyContent: 'center',
                zIndex: 1,
                '&:hover': {
                  backgroundColor: 'background.paper',
                },
              }}
            >
              <Button
                content="Done"
                variant="primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShown(false);
                }}
                sx={{
                  width: '100%',
                }}
              />
            </MenuItem>
          </Select>
          {(error || hint) && (
            <Typography
              variant="caption"
              color={error ? 'error' : 'text.secondary'}
              sx={{ mt: 0.5, ml: 1.75 }}
            >
              {error || hint}
            </Typography>
          )}
        </FormControl>
      )}
    </Field>
  );
}
